import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Translator } from '@vectronic/util';

@Component({
  selector: 'table-datatable-input',
  templateUrl: './datatable-input.component.html',
  styleUrls: ['./datatable-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class DatatableInputComponent {
  value = input<string>('');
  editable = input<boolean>(false);
  multiline = input<boolean>(false);
  placeholder = input<string>('Enter value…');
  emptySymbol = input<string>('—');
  trim = input<number | undefined>(undefined);

  valueChange = output<string>();

  protected readonly isEditing = signal(false);
  protected readonly draft = signal('');

  private readonly inputRef =
    viewChild<ElementRef<HTMLInputElement | HTMLTextAreaElement>>('inputRef');
  private readonly injector = inject(Injector);

  protected readonly isEmpty = computed(() => !this.value());

  protected readonly isTrimmed = computed(() => {
    const trim = this.trim();
    return !!trim && (this.value()?.length ?? 0) > trim;
  });

  protected readonly displayValue = computed(() => {
    const trim = this.trim();
    const val = this.value() ?? '';
    return this.isTrimmed() ? Translator.trimString(val, trim!) : val;
  });

  protected onInput(event: Event): void {
    this.draft.set((event.target as HTMLInputElement | HTMLTextAreaElement).value);
  }

  protected startEdit(): void {
    this.draft.set(this.value() ?? '');
    this.isEditing.set(true);
    afterNextRender(() => this.inputRef()?.nativeElement.focus(), { injector: this.injector });
  }

  protected cancelEdit(): void {
    this.isEditing.set(false);
    this.draft.set('');
  }

  protected submit(): void {
    this.valueChange.emit(this.draft());
    this.isEditing.set(false);
  }
}
